import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, Upload, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fileViewUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  authorized_name: "", relationship: "", document_type: "",
  document_number: "", phone: "", email: "", notes: "", id_document_id: "",
};

export function FamilyAuthTab({ order, onUpdated }) {
  const { user } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [idDoc, setIdDoc] = useState(null);
  const fileRef = useRef(null);
  const canEdit = user?.role !== "driver";

  useEffect(() => {
    if (order?.family_authorization) setForm({ ...EMPTY, ...order.family_authorization });
  }, [order?.id]);

  useEffect(() => {
    api.get(`/documents`, { params: { entity_type: "order", entity_id: order.id } })
      .then(({ data }) => setIdDoc(data.find((d) => d.category === "family_authorization") || null))
      .catch(() => {});
  }, [order.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.authorized_name) { toast.error("Inserisci il nome della persona autorizzata"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      const { data } = await api.patch(`/orders/${order.id}/family-authorization`, payload);
      toast.success("Autorizzazione salvata");
      onUpdated?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadId = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file); fd.append("entity_type", "order");
    fd.append("entity_id", order.id); fd.append("category", "family_authorization");
    try {
      const { data } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIdDoc(data); set("id_document_id", data.id);
      toast.success("Documento d'identità caricato");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="family-auth-tab">
      <p className="text-sm text-slate-500">Dati della persona autorizzata dalla famiglia a gestire il rimpatrio.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Nome autorizzato *"><Input value={form.authorized_name} disabled={!canEdit} onChange={(e) => set("authorized_name", e.target.value)} data-testid="fa-name" /></Field>
        <Field label="Relazione col defunto"><Input value={form.relationship} disabled={!canEdit} onChange={(e) => set("relationship", e.target.value)} data-testid="fa-relationship" /></Field>
        <Field label="Tipo documento"><Input value={form.document_type} disabled={!canEdit} onChange={(e) => set("document_type", e.target.value)} data-testid="fa-doc-type" /></Field>
        <Field label="Numero documento"><Input value={form.document_number} disabled={!canEdit} onChange={(e) => set("document_number", e.target.value)} data-testid="fa-doc-number" /></Field>
        <Field label="Telefono"><Input value={form.phone} disabled={!canEdit} onChange={(e) => set("phone", e.target.value)} data-testid="fa-phone" /></Field>
        <Field label="Email"><Input value={form.email} disabled={!canEdit} onChange={(e) => set("email", e.target.value)} data-testid="fa-email" /></Field>
      </div>
      <Field label="Note"><Textarea value={form.notes} disabled={!canEdit} onChange={(e) => set("notes", e.target.value)} data-testid="fa-notes" /></Field>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="text-sm">
            <div className="font-medium text-slate-700">Documento d'identità</div>
            <div className="text-xs text-slate-400">{idDoc ? idDoc.original_filename : "Nessun documento caricato"}</div>
          </div>
          <div className="flex gap-2">
            {idDoc && <a href={fileViewUrl(idDoc.id)} target="_blank" rel="noreferrer"><Button variant="outline" size="sm" data-testid="fa-view-id"><Eye size={14} className="mr-1" /> Vedi</Button></a>}
            {canEdit && <>
              <input ref={fileRef} type="file" className="hidden" onChange={uploadId} data-testid="fa-id-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="fa-upload-id"><Upload size={14} className="mr-1" /> Carica</Button>
            </>}
          </div>
        </div>
      </div>

      {canEdit && (
        <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="fa-save-btn">
          <Save size={16} className="mr-1.5" /> {saving ? "Salvataggio…" : "Salva autorizzazione"}
        </Button>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
